<script lang="ts" setup>
  import { selectContentEvent, useGoogleTagManager } from '@storefront/storefront';

  const gtm = useGoogleTagManager();

  defineProps({
    post: { type: Object, required: true },
    horizontal: { type: Boolean, required: false, default: false },
    header: { type: String, required: false, default: 'h3' },
  });

  function getAlt(post: any) {
    return post.photoAlt || post.title;
  }
</script>
<template>
  <article class="article" :class="{ horizontal }">
    <RouterLink :href="$route('web.blog.post.show', { post })" @click="gtm.push(selectContentEvent('blogPost', post.title))" class="article__link">
      <MediaPicture :alt="getAlt(post)" :photo="post.photo" sizes="1080x800 2x, 540x400" class="article__image" />
      <div class="article__content">
        <component :is="header" class="article__heading">
          {{ post.title }}
        </component>
        <div class="article__labels">
          <span v-if="post.categoryTitle" class="article__category">
            <SfIconBookmark aria-hidden="true" />
            {{ post.categoryTitle }}
          </span>
          <span v-if="post.date" class="article__data">
            <SfIconClock aria-hidden="true" />
            <SfDateTime :value="post.date" />
          </span>
        </div>
        <div class="article__description" v-html="post.descriptionShort" />
      </div>
    </RouterLink>
  </article>
</template>
<style lang="scss">
  :root {
    --site-article-border: 1px solid var(--secondary-border);
  }
</style>
<style lang="scss" scoped>
  .article {
    border: var(--site-article-border);
    border-radius: var(--border-radius);
    overflow: hidden;
    transition-property: border-color;
    transition-timing-function: var(--transition-primary-timing-function);
    transition-duration: var(--transition-primary-duration);
    border-radius: var(--border-radius);
    overflow: hidden;

    @media(hover: hover) {
      &:hover {
        border-color: var(--primary-border-hover);
      }
    }

    &__link {
      display: flex;
      flex-direction: column;

      &:focus-visible {
        outline: 2px solid;
        outline-offset: -3px;
      }
    }

    :deep(img) {
      width: 100%;
      object-fit: cover;
      aspect-ratio: 540/400;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem
    }

    &__heading {
      font-size: 1rem;
    }

    &__labels {
      display: flex;
      flex-wrap: wrap;
      gap: 0.125rem 1rem;
      font-size: 0.75rem;
    }

    &__description {
      font-size: 0.875rem;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;

      :deep(p) {
        font-size: 0.875rem;
      }
    }

    &__data,
    &__category {
      display: inline-flex;
      gap: 0.25rem;
      align-items: center;
    }


    @media(min-width: 993px) {
      &.horizontal {
        .article__link {
          display: grid;
          grid-template-columns: 200px 1fr;

          :deep(img) {
            width: 200px;
            height: 100%;
          }
        }
      }
    }

    @media(max-width: 992px) {
      &__content {
        padding: 0.5rem;
      }

      &__heading {
        font-size: 0.875rem;
      }

      &__labels {
        font-size: 0.75rem;
      }

      &__description {
        font-size: 0.75rem;

        :deep(p) {
          font-size: 0.75rem;
        }
      }
    }
  }
</style>
